/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import {ColumnState} from 'ag-grid-community';
import debounce from 'lodash.debounce';
import isEqual from 'lodash.isequal';
import isNil from 'lodash.isnil';
import {isDesktop} from '@ion/core/src/capabilities/device';
import {error} from '@ion/core/src/logger/logger';
import UserPreferences from '@ion/core/src/preferences/UserPreferences';
import {GenericObject} from '@ion/core/src/types/entities';
import {JobSearchEngineParams, LoadDevicesStatusParams} from '@ion/core/src/types/services';

const PAGE_ID = 1;

const PAGE_SIZE = 100;

// list of time/date based columns to be ignored for sorting when exporting data from grids
export const timeBasedColumnNames = ['event', 'modified', 'created', 'activated'];

/**
 * Provides a utility which allows for determining if the UserPreferences API has been
 * initialized and if the given preference is defined.
 */
export const isPreferenceAvailable = (preference?: string) => {
  return UserPreferences.isInitialized() && (preference ? UserPreferences.get(preference) : true);
};

/**
 * @private
 *
 * Provides a convenience function which determines and returns the standardized grid
 * columns preferences path.
 */
const getPreferencePath = (page: string): string => {
  return `${page}:grid.columns.${isDesktop() ? 'desktop' : 'mobile'}`;
};

/**
 * The utility for save grid column state in user preferences based on path.
 *
 *  ```typescript
 *
 *    import {saveGridColumnState} from '../helpers';
 *
 *    const PREFERENCE = 'devicetypes';
 *
 *    saveGridColumnState(PREFERENCE, columnState);
 *
 * ```
 *
 * @param page The user preferences path for the page
 * @param state The current state of the grid columns.
 * @returns The persisted column state for the given grid.
 */
export const saveGridColumnState = (page: string, state: ColumnState[]): Promise<boolean> => {
  return UserPreferences.set(getPreferencePath(page), state, !UserPreferences.hasChangeDetection());
};

/**
 * The utility function compares the previous and next state of the Grid column state.
 *
 *  ```typescript
 *
 *    import {hasChangeInColumnState} from '../helpers';
 *
 *    const hasChange = hasChangeInColumnState(preferedColumnState, nextColumnState);
 *
 * ```
 *
 * @param preState The previous grid column state
 * @param nxtState The next grid column state
 * @returns returns true if the state has change, else false
 */
export const hasChangeInColumnState = (preState: ColumnState[], nxtState: ColumnState[]): boolean => {
  return JSON.stringify(preState) !== JSON.stringify(nxtState);
};

/**
 * Given a specific iOn Suite Grid columns path, and the current state of the grid
 * columns for the given path, persists the column state to the path if the user has
 * chosen to persist their Grid Column selections, otherwise returns false.
 *
 * ```typescript
 *
 *    import {getGridColumnState, setGridColumnState} from '../helpers';
 *
 *    const PREFERENCE = 'devicetypes';
 *    ...
 *
 *    [SET_GRID_COLUMNS] : ({grid}: DeviceTypesState, {columnState}) => {
 *      grid.columnState = columnState;
 *      setGridColumnState(PREFERENCE, columnState);
 *    }
 *
 * ```
 *
 * @param   path The user preferences path for the given page
 * @param   state The current state of the grid columns.
 * @param   autoSaveGrid Explicit check of auto save grid settings in user preference.
 * @returns The persisted column state for the given grid.
 */
export const setGridColumnState = debounce((
  page: string,
  state: ColumnState[],
  autoSaveGrid: boolean = false
): Promise<boolean> => {
  if (autoSaveGrid || isPreferenceAvailable('persistGridLayouts')) {
    return saveGridColumnState(page, state);
  }
  return Promise.resolve(false);
}, 3000);

/**
 * The utility for set grid column state in redux and user preferences based on path
 * if the user enabled the Automatic Grid saving option.
 *
 *  ```typescript
 *
 *    import {onSetGridColumns} from '../helpers';
 *
 *    const PREFERENCE = 'deviceTypes';
 *
 *    onSetGridColumns({
 *      grid,
 *      isLoading  : devicetypes.loading,
 *      preference : PREFERENCE
 *    }, columnState);
 *
 * ```
 *
 * @param grid grid redux state
 * @param isLoading indicates changes while loading
 * @param preference page preference.
 * @param columnState column state which holds the previous and current state
 */
export const onSetGridColumns = ({grid, isLoading, preference}, columnState) => {
  const {
    preferedColumnState,
    nextColumnState,
    autoSaveGrid
  }                = columnState;
  const preCheck   = !isLoading && !autoSaveGrid;
  if (!isLoading) {
    grid.hasUpdate = preCheck && hasChangeInColumnState(preferedColumnState, nextColumnState);
  }
  grid.columnState = nextColumnState;
  setGridColumnState(preference, nextColumnState, autoSaveGrid);
  autoSaveGrid && (grid.preferedColumnState = nextColumnState);
};

/**
 * The utility for set grid column state in redux and user preferences based on path.
 *
 *  ```typescript
 *
 *    import {onUpdateGridColumns} from '../helpers';
 *
 *    const PREFERENCE = 'devicetypes';
 *
 *    onUpdateGridColumns({
 *      grid,
 *      preference : PREFERENCE
 *    }, columnState);
 *
 * ```
 *
 * @param grid grid redux state
 * @param preference page preference.
 * @param columnState column state which holds the previous and current state
 */
export const onUpdateGridColumns = ({grid, preference}, columnState) => {
  const {nextColumnState}   = columnState;
  grid.hasUpdate            = false;
  grid.preferedColumnState  = nextColumnState;
  saveGridColumnState(preference, nextColumnState);
};

/**
 * Given a specific iOn Suite Grid columns path, if the user has chosen to
 * persist their Grid Column selections across sessions and devices, this
 * function will return the user persisted column selections and state for
 * the given grid (if any).
 *
 * ```typescript
 *
 *    import {getGridColumnState, setGridColumnState} from '../helpers';
 *
 *    const PREFERENCE = 'devicetypes';
 *    ...
 *
 *    [INITIALIZE_DEVICE_TYPES] : (draft) => {
 *      draft.isInitialized = true;
 *      draft.grid.columnState = getGridColumnState(PREFERENCE);
 *    }
 *
 * ```
 *
 * @param   path The user preferences path for the given grid;
 * @param   forceFetch Allow to fetching the grid setting in user preference forcefully
 * @returns The persisted column state for the given grid.
 */
export const getGridColumnState = (page: string, forceFetch: boolean = false): ColumnState[] | undefined => {
  if (isPreferenceAvailable('persistGridLayouts') || forceFetch) {
    return UserPreferences.get(getPreferencePath(page)) as ColumnState[];
  }
};

/**
 * The method helps that sets the current grid column state in redux state and if
 * the user has auto grid save preference in extended attribute this
 * will set the column state in user preference. This can also resuable when the force update
 * done by the user using the grid save option which is on top of grid.
 *
 * ```typescript
 *
 *  import {dispatchGridColumnsChange}  from '@/store/actions/helper';
 *
 *  const dispatch = useDispatch();
 *  dispatch(dispatchGridColumnsChange(columns, 'adminAssets', 'SET_GRID_COLUMNS'));
 *
 * ```
 *
 * @param columnState The Current column state from grid.
 * @param reducer     A name of the reducer. ex. adminAssets.
 * @param type        The type of an action.
 */
export const dispatchGridColumnsChange = (
  columnState: ColumnState[],
  reducer: string,
  type: string
) => {
  return (dispatch, getState) => {
    if (columnState) {
      const {[reducer]: {grid}} = getState();
      const autoSaveGrid        = isPreferenceAvailable('persistGridLayouts');
      dispatch({
        columnState : {
          autoSaveGrid,
          preferedColumnState : grid?.preferedColumnState,
          nextColumnState     : columnState
        },
        type
      });
    }
  };
};

/**
 * The utility for sorting column selector in the grid based on the selection.
 */
export const sortColumns = (columns) => {
  const unsortedColumn = columns.filter(({hide}) => isEqual(hide, false));
  const sortedColumn   = columns.filter(
    ({hide}) => isEqual(hide, true)).sort(
    (a, b) => (a.headerName === b.headerName) ? 0 : ((a.headerName > b.headerName) ? 1 : -1));
  return unsortedColumn.concat(sortedColumn);
};

/**
 * The utility for setting the sort param as name instead of any date/time related column in case of export.
 */
export const adjustSortField = (params: GenericObject, sortParam: String) => {
  const sortField = params?.sortOn?.[0]?.toLowerCase();
  if (!sortField) return;

  if (timeBasedColumnNames.some(column => sortField.includes(column))) {
    params.sortOn[0] = sortParam;
  }
};

/**
 * The utility performs multiple asynchronous API calls in parallel to retrieve details
 * from the provided API based on the passed params.
 * 
 * @param {string} api - The API endpoint to send the request to.
 * @param {Array} jobs - The data will be used to construct the API payload.
 * @param {string} sort - Sorting parameter (e.g., 'asc' or 'desc').
 * @param {number} accountId - Current group id.
 * @param {boolean} includeSubGroups - Whether to include devices from subaccounts 
 *      in the search (default is false)
 * @param {boolean} isJobSearchEngine - To construct payload based on legacy or beta Job.
 * 
 * 1. The utility accepts the API endpoint and parameters
 * 2. The utility constructs payload based on legacy or beta job for each API call based 
 *    on the data array and parameters.
 * 3. The API calls are made in parallel using Promise.all().
 * 4. The results are returned as an array.
 * 5. If task index isn't available null or undefined is returned where the export fails
 *    with displaying a snack bar message.
 */
export const loadAllData = async({
  api,
  jobs,
  sortOn = null,
  accountId = null,
  includeSubGroups = false,
  isJobSearchEngine = false
}) => {
  const {
    id, jobId, tasks
  } = jobs || {};

  const indices = tasks?.map(({taskIndex, task}) => {
    return taskIndex ?? task?.index;
  });

  try {
    let results;

    if (!isNil(indices)) {
      results = await Promise.all(indices.map(async(index) => {
        let jobData: LoadDevicesStatusParams | JobSearchEngineParams;
  
        const pageParams = {
          pageId   : PAGE_ID,
          pageSize : PAGE_SIZE
        };
  
        if (isJobSearchEngine) {
          jobData = {
            query : `jobId: ${jobId} AND taskIndex: ${index}`,
            includeSubGroups,
            accountId,
            sortOn,
            ...pageParams
          };
        } else {
          jobData = {
            accountId : id,
            index,
            ...pageParams
          };
        }
  
        try {
          return await api(jobData);
        } catch (err) {
          error(err);
        }
      }));
    }

    return results;
  } catch (err) {
    error(err);
  }
};
